import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API from '../api/axios';
import toast from 'react-hot-toast';

// ─── Password strength helpers ────────────────────────────────────────────────
function getStrength(pw) {
  let s = 0;
  if (pw.length >= 6) s++;
  if (pw.length >= 10) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return s;
}
const STRENGTH_LABEL = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
const STRENGTH_COLOR = ['', '#e74c3c', '#e67e22', '#f1c40f', '#27ae60', '#1abc9c'];

// ─── Minimal shared input style factory ──────────────────────────────────────
const inp = (extra = {}) => ({
  width: '100%',
  padding: '13px 16px',
  background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(255,255,255,0.14)',
  borderRadius: '12px',
  color: '#ffffff',
  fontSize: '14px',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  ...extra,
});

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const firstRef = useRef(null);

  // page state: 'verifying' | 'ready' | 'error' | 'done'
  const [phase, setPhase] = useState('verifying');
  const [email, setEmail]  = useState('');
  const [errMsg, setErrMsg] = useState('');

  // form
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', password: '', confirm: '',
  });
  const [showPw,  setShowPw]      = useState(false);
  const [showCfm, setShowCfm]     = useState(false);
  const [saving,  setSaving]      = useState(false);

  const strength = getStrength(form.password);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // ── Verify the token on mount ───────────────────────────────────────────────
  const verify = useCallback(async () => {
    if (!token) {
      setErrMsg('No invitation token found. Please use the exact link from your email.');
      setPhase('error');
      return;
    }
    try {
      const res = await API.get(`/auth/verify-invite/${token}`);
      setEmail(res.data.email || '');
      setPhase('ready');
    } catch (err) {
      setErrMsg(
        err.response?.data?.message ||
        'This invitation link is invalid or has expired. Please ask admin for a new invite.'
      );
      setPhase('error');
    }
  }, [token]);

  useEffect(() => { verify(); }, [verify]);

  // Auto-focus first name when form is ready
  useEffect(() => {
    if (phase === 'ready') {
      setTimeout(() => firstRef.current?.focus(), 100);
    }
  }, [phase]);

  // ── Submit handler ──────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const { firstName, lastName, phone, password, confirm } = form;

    if (!firstName.trim())           return toast.error('Please enter your first name.');
    if (!/^\d{10}$/.test(phone))     return toast.error('Enter a valid 10-digit mobile number.');
    if (password.length < 6)         return toast.error('Password must be at least 6 characters.');
    if (password !== confirm)        return toast.error('Passwords do not match.');

    setSaving(true);
    try {
      const res = await API.post('/auth/accept-invite', {
        token, firstName, lastName, phone, password,
      });

      // Persist auth — exactly like login does
      localStorage.setItem('numToken', res.data.token);
      localStorage.setItem('numUser', JSON.stringify(res.data.user));

      setPhase('done');

      // Short delay so "done" screen is visible, then hard-navigate to dashboard
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 2000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Shared focus / blur handlers for inputs ─────────────────────────────────
  const onFocus = (e) => {
    e.target.style.borderColor  = 'rgba(108,63,197,0.8)';
    e.target.style.boxShadow    = '0 0 0 3px rgba(108,63,197,0.18)';
  };
  const onBlur  = (e) => {
    e.target.style.borderColor  = 'rgba(255,255,255,0.14)';
    e.target.style.boxShadow    = 'none';
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(140deg, #0d0d1a 0%, #180a32 55%, #0a0d1f 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        @keyframes fadeUp  { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes popIn   { 0%{transform:scale(0.4);opacity:0} 100%{transform:scale(1);opacity:1} }
        @keyframes spin    { to{transform:rotate(360deg)} }
        @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:0.5} }
        .ai-btn:hover:not(:disabled) { opacity:0.85; transform:translateY(-1px); }
        .ai-btn:active:not(:disabled) { transform:translateY(0); }
        .ai-btn:disabled { opacity:0.55; cursor:not-allowed; }
        .ai-card { animation: fadeUp 0.35s ease both; }
      `}</style>

      <div className="ai-card" style={{
        background: 'rgba(255,255,255,0.04)',
        backdropFilter: 'blur(28px)',
        WebkitBackdropFilter: 'blur(28px)',
        border: '1px solid rgba(255,255,255,0.09)',
        borderRadius: '24px',
        padding: '44px 40px',
        width: '100%', maxWidth: '480px',
        boxShadow: '0 40px 100px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
        textAlign: 'center',
      }}>

        {/* ── VERIFYING ──────────────────────────────────────────────────────── */}
        {phase === 'verifying' && (
          <>
            <div style={{ fontSize: 48, marginBottom: 20 }}>🔢</div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: '0 0 10px' }}>
              Verifying your invitation…
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 14 }}>
              Hang tight, we're checking your invite link.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 28 }}>
              <div style={{
                width: 36, height: 36,
                border: '3px solid rgba(108,63,197,0.25)',
                borderTopColor: '#7c4dff',
                borderRadius: '50%',
                animation: 'spin 0.9s linear infinite',
              }} />
            </div>
          </>
        )}

        {/* ── ERROR ──────────────────────────────────────────────────────────── */}
        {phase === 'error' && (
          <>
            <div style={{ fontSize: 52, marginBottom: 16 }}>⛔</div>
            <h1 style={{ fontSize: 21, fontWeight: 800, color: '#ff6b6b', margin: '0 0 12px' }}>
              Invitation Expired or Invalid
            </h1>
            <div style={{
              background: 'rgba(231,76,60,0.1)',
              border: '1px solid rgba(231,76,60,0.3)',
              borderRadius: '14px',
              padding: '18px',
              marginBottom: 24,
            }}>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, lineHeight: 1.65, margin: 0 }}>
                {errMsg}
              </p>
            </div>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>
              Contact{' '}
              <a href="mailto:care@aakritishealverse.com"
                style={{ color: '#a78bfa', textDecoration: 'none', fontWeight: 600 }}>
                support
              </a>{' '}
              to request a new link.
            </p>
          </>
        )}

        {/* ── READY — Customer fills details ──────────────────────────────────── */}
        {phase === 'ready' && (
          <>
            {/* Header */}
            <div style={{
              width: 64, height: 64, borderRadius: '18px',
              background: 'linear-gradient(135deg,#6c3fc5,#4a1fa8)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 20px', fontSize: 28,
              boxShadow: '0 10px 36px rgba(108,63,197,0.4)',
            }}>
              🎉
            </div>

            <h1 style={{ fontSize: 23, fontWeight: 800, color: '#fff', margin: '0 0 8px' }}>
              You've been invited!
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 14, margin: '0 0 18px', lineHeight: 1.6 }}>
              Complete your profile below to activate your account.
            </p>

            {/* Email badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: 'rgba(108,63,197,0.18)',
              border: '1px solid rgba(108,63,197,0.35)',
              borderRadius: '10px',
              padding: '7px 16px',
              marginBottom: 28,
              fontSize: 13, color: '#c4b5fd', fontWeight: 600,
            }}>
              📧 {email}
            </div>

            <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>

              {/* Name row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                {[
                  { key: 'firstName', label: 'First Name *', ph: 'Rahul', ref: firstRef },
                  { key: 'lastName',  label: 'Last Name',    ph: 'Sharma' },
                ].map(({ key, label, ph, ref }) => (
                  <div key={key}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 7 }}>
                      {label}
                    </label>
                    <input
                      ref={ref || undefined}
                      id={`invite-${key}`}
                      type="text"
                      placeholder={ph}
                      value={form[key]}
                      onChange={set(key)}
                      onFocus={onFocus}
                      onBlur={onBlur}
                      autoComplete={key === 'firstName' ? 'given-name' : 'family-name'}
                      style={inp()}
                    />
                  </div>
                ))}
              </div>

              {/* Phone */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 7 }}>
                  Mobile Number *
                </label>
                <input
                  id="invite-phone"
                  type="tel"
                  placeholder="10-digit mobile number"
                  value={form.phone}
                  onChange={set('phone')}
                  onFocus={onFocus}
                  onBlur={(e) => {
                    onBlur(e);
                    if (form.phone && !/^\d{10}$/.test(form.phone)) {
                      e.target.style.borderColor = 'rgba(231,76,60,0.7)';
                    }
                  }}
                  maxLength={10}
                  autoComplete="tel"
                  style={inp({
                    borderColor: form.phone && !/^\d{10}$/.test(form.phone)
                      ? 'rgba(231,76,60,0.7)' : 'rgba(255,255,255,0.14)',
                  })}
                />
                {form.phone && !/^\d{10}$/.test(form.phone) && (
                  <p style={{ fontSize: 11, color: '#e74c3c', marginTop: 5, marginBottom: 0 }}>
                    Enter a valid 10-digit number
                  </p>
                )}
              </div>

              {/* Password */}
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 7 }}>
                  Create Password *
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="invite-password"
                    type={showPw ? 'text' : 'password'}
                    placeholder="Min. 6 characters"
                    value={form.password}
                    onChange={set('password')}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    minLength={6}
                    autoComplete="new-password"
                    style={inp({ paddingRight: 46 })}
                  />
                  <button type="button"
                    onClick={() => setShowPw(v => !v)}
                    aria-label="Toggle password visibility"
                    style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 17, padding: 0, color: 'rgba(255,255,255,0.4)' }}>
                    {showPw ? '🙈' : '👁️'}
                  </button>
                </div>
                {form.password.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${(strength / 5) * 100}%`,
                        background: STRENGTH_COLOR[strength],
                        borderRadius: 2,
                        transition: 'width 0.3s, background 0.3s',
                      }} />
                    </div>
                    <p style={{ fontSize: 11, color: STRENGTH_COLOR[strength], marginTop: 4, textAlign: 'right', fontWeight: 600 }}>
                      {STRENGTH_LABEL[strength]}
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 7 }}>
                  Confirm Password *
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="invite-confirm"
                    type={showCfm ? 'text' : 'password'}
                    placeholder="Re-enter your password"
                    value={form.confirm}
                    onChange={set('confirm')}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    autoComplete="new-password"
                    style={inp({
                      paddingRight: 46,
                      borderColor: form.confirm
                        ? form.password === form.confirm
                          ? 'rgba(39,174,96,0.7)'
                          : 'rgba(231,76,60,0.7)'
                        : 'rgba(255,255,255,0.14)',
                    })}
                  />
                  <button type="button"
                    onClick={() => setShowCfm(v => !v)}
                    aria-label="Toggle confirm password"
                    style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 17, padding: 0, color: 'rgba(255,255,255,0.4)' }}>
                    {showCfm ? '🙈' : '👁️'}
                  </button>
                </div>
                {form.confirm && form.password !== form.confirm && (
                  <p style={{ fontSize: 11, color: '#e74c3c', marginTop: 5, marginBottom: 0 }}>Passwords don't match</p>
                )}
                {form.confirm && form.password === form.confirm && (
                  <p style={{ fontSize: 11, color: '#27ae60', marginTop: 5, marginBottom: 0 }}>✓ Passwords match</p>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="ai-btn"
                id="invite-submit"
                disabled={saving}
                style={{
                  width: '100%',
                  padding: '15px',
                  borderRadius: '13px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #6c3fc5 0%, #4a1fa8 100%)',
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 700,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                  boxShadow: '0 8px 28px rgba(108,63,197,0.4)',
                  transition: 'opacity 0.2s, transform 0.15s',
                  fontFamily: 'inherit',
                  letterSpacing: '0.2px',
                }}>
                {saving ? (
                  <>
                    <div style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                    Activating…
                  </>
                ) : (
                  '🔐 Activate My Account'
                )}
              </button>
            </form>

            <p style={{ marginTop: 20, fontSize: 12, color: 'rgba(255,255,255,0.25)' }}>
              Already have an account?{' '}
              <a href="/login" style={{ color: '#a78bfa', textDecoration: 'none', fontWeight: 600 }}>
                Sign in
              </a>
            </p>
          </>
        )}

        {/* ── DONE ───────────────────────────────────────────────────────────── */}
        {phase === 'done' && (
          <div style={{ padding: '12px 0' }}>
            <div style={{
              width: 80, height: 80, borderRadius: '50%',
              background: 'linear-gradient(135deg,#27ae60,#1e8449)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 22px', fontSize: 36,
              boxShadow: '0 12px 40px rgba(39,174,96,0.4)',
              animation: 'popIn 0.45s cubic-bezier(0.175,0.885,0.32,1.275)',
            }}>
              ✅
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: '0 0 10px' }}>
              Account Activated!
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 14, lineHeight: 1.65 }}>
              Welcome to HealVerse Numerology 🎉<br />
              Taking you to your dashboard…
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 22 }}>
              <div style={{
                width: 28, height: 28,
                border: '3px solid rgba(108,63,197,0.25)',
                borderTopColor: '#7c4dff',
                borderRadius: '50%',
                animation: 'spin 0.9s linear infinite',
              }} />
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
